import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { User } from '../../models/User';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/macro';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { X, Search, UserPlus } from 'lucide-react';

interface UserSelectModalProps {
    show: boolean;
    title: string;
    users: User[];
    onClose: () => void;
    onSelect: (user: User) => void;
}

const UserSelectModal: React.FC<UserSelectModalProps> = ({ show, title, users, onClose, onSelect }) => {
    const { i18n } = useLingui();
    const [searchText, setSearchText] = useState('');

    const filteredUsers = useMemo(() => {
        if (!searchText) return users;
        return users.filter(user =>
            user.name.toLowerCase().includes(searchText.toLowerCase()) ||
            user.email.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [users, searchText]);

    return (
        <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md p-0 overflow-hidden flex flex-col max-h-[80vh]">
                <DialogHeader className="p-4 border-b">
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                {/* Search - Sticky */}
                <div className="p-4 bg-white border-b border-gray-100 sticky top-0 z-10 shrink-0">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder={t`Search members`}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="pl-9 bg-gray-50 border-gray-200 focus-visible:ring-blue-500"
                        />
                    </div>
                </div>

                {/* List */}
                <div className="overflow-y-auto p-2 flex-1">
                    {filteredUsers.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 text-sm">
                            {t`No members found`}
                        </div>
                    ) : (
                        <ul className="space-y-1">
                            {filteredUsers.map(user => (
                                <li key={user.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors group">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="h-10 w-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                            {user.photoUrl ? (
                                                <img src={user.photoUrl} alt={user.name} className="h-full w-full object-cover" />
                                            ) : (
                                                <div className="h-full w-full flex items-center justify-center bg-blue-100 text-blue-600 font-bold text-sm">
                                                    {user.name.charAt(0)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-gray-900 truncate">{user.name}</span>
                                            <span className="text-xs text-gray-500 truncate">{user.email}</span>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="default"
                                        onClick={() => onSelect(user)}
                                        className="bg-blue-600 text-white shadow-sm h-8 px-3"
                                    >
                                        <UserPlus className="h-4 w-4 mr-1.5" />
                                        {t`Invite`}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default UserSelectModal;
